// ICS (iCalendar) export/import — NO OAuth, file-based interchange only (§4.9).
// Export a BIDIT calendar Gmail/Outlook accept; import an exported Outlook/Gmail
// file (events become view-only; re-import updates rather than duplicates).

function pad(n) { return String(n).padStart(2, '0'); }

/** Format a Date (or ISO string) as an ICS UTC timestamp: YYYYMMDDTHHMMSSZ */
export function toIcsDate(dateOrIso) {
  const d = new Date(dateOrIso);
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) + 'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) + 'Z'
  );
}

function escapeText(s = '') {
  return String(s)
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

function eventStart(e) {
  return e.start || e.startDate || e.date || e.time;
}
function eventEnd(e) {
  return e.end || e.endDate || e.start || e.startDate || e.date || e.time;
}

// IDI-870: the calendar's own day + "HH:MM" wall-clock fields, written as
// floating local time (D1) — built from string parts, never through UTC.
function bareDay(s) {
  return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
}
function hhmm(s) {
  if (typeof s !== 'string') return null;
  const m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!m) return null;
  const h = +m[1];
  const mi = +m[2];
  if (h > 23 || mi > 59) return null;
  return h * 60 + mi;
}
/** Add n local calendar days to "YYYY-MM-DD" (DST-safe, like addDaysYmd). */
function addDays(day, n) {
  const [y, m, d] = day.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}
function icsDay(day) { return day.replace(/-/g, ''); }
/** "YYYY-MM-DD" + minutes from midnight (may run past 24h) → YYYYMMDDTHHMM00. */
function floating(day, minutes) {
  const carry = Math.floor(minutes / 1440);
  const rest = minutes - carry * 1440;
  const d = carry ? addDays(day, carry) : day;
  return `${icsDay(d)}T${pad(Math.floor(rest / 60))}${pad(rest % 60)}00`;
}

/**
 * The DTSTART/DTEND lines for an event, as the calendar draws it (IDI-870).
 * D2 all day: VALUE=DATE, DTEND the day after the last day.
 * D1/D3 timed: floating local; end = the stop; no stop, +1h; a same-day stop
 *   before the start, midnight; a span, its last day's stop, else midnight after it.
 * D4 anything else (an imported ISO instant): today's UTC form.
 */
function icsTimes(e) {
  const startDay = bareDay(e.date) || bareDay(e.start) || bareDay(e.startDate);
  if (startDay) {
    // Last day: a WO card's `end`, else an appointment's `endDate`.
    const endCandidate = bareDay(e.end) || bareDay(e.endDate) || bareDay(e.stopDate);
    const lastDay = endCandidate && endCandidate > startDay ? endCandidate : startDay;
    if (e.allDay) {
      return [
        `DTSTART;VALUE=DATE:${icsDay(startDay)}`,
        `DTEND;VALUE=DATE:${icsDay(addDays(lastDay, 1))}`,
      ];
    }
    const startMin = hhmm(e.startTime);
    if (startMin !== null) {
      const stopMin = hhmm(e.stopTime || e.endTime);
      let end;
      if (lastDay > startDay) {
        end = stopMin !== null ? floating(lastDay, stopMin) : floating(addDays(lastDay, 1), 0);
      } else if (stopMin === null) {
        end = floating(startDay, startMin + 60);
      } else if (stopMin < startMin) {
        end = floating(addDays(startDay, 1), 0);
      } else {
        end = floating(startDay, stopMin);
      }
      return [`DTSTART:${floating(startDay, startMin)}`, `DTEND:${end}`];
    }
  }
  return [`DTSTART:${toIcsDate(eventStart(e))}`, `DTEND:${toIcsDate(eventEnd(e))}`];
}

/** A stable UID so re-export/re-import doesn't duplicate (§4.9). */
export function eventUid(e) {
  return `${e.id || e.uid || Math.random().toString(36).slice(2)}@bidit`;
}

/** Build a full ICS document string from BIDIT events. */
export function eventsToIcs(events = [], calName = 'BIDIT') {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BIDIT//Calendar//EN',
    'CALSCALE:GREGORIAN',
    `X-WR-CALNAME:${escapeText(calName)}`,
  ];
  events.forEach((e) => {
    lines.push('BEGIN:VEVENT');
    lines.push(`UID:${eventUid(e)}`);
    lines.push(`DTSTAMP:${toIcsDate(new Date())}`);
    lines.push(...icsTimes(e));
    lines.push(`SUMMARY:${escapeText(e.title || 'Untitled')}`);
    if (e.location) lines.push(`LOCATION:${escapeText(e.location)}`);
    if (e.notes || e.description) lines.push(`DESCRIPTION:${escapeText(e.notes || e.description)}`);
    lines.push('END:VEVENT');
  });
  lines.push('END:VCALENDAR');
  return lines.join('\r\n');
}

/** Trigger a browser download of the ICS file. */
export function downloadIcs(events, filename = 'bidit-calendar.ics') {
  const ics = eventsToIcs(events);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function parseIcsDate(val = '') {
  // YYYYMMDDTHHMMSSZ or YYYYMMDD
  const m = val.match(/(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2}))?/);
  if (!m) return null;
  const [, y, mo, d, h = '00', mi = '00', s = '00'] = m;
  const isUtc = /Z$/.test(val);
  if (isUtc) return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s)).toISOString();
  return new Date(+y, +mo - 1, +d, +h, +mi, +s).toISOString();
}

function unescapeText(s = '') {
  return s.replace(/\\n/g, '\n').replace(/\\,/g, ',').replace(/\\;/g, ';').replace(/\\\\/g, '\\');
}

/**
 * Parse an ICS file into BIDIT events. Imported events are marked
 * `source: 'imported'` and `readOnly: true` (view-only — §4.9).
 */
export function parseIcs(text = '') {
  const events = [];
  const blocks = text.split('BEGIN:VEVENT').slice(1);
  blocks.forEach((block) => {
    const body = block.split('END:VEVENT')[0];
    const get = (key) => {
      const re = new RegExp(`${key}[^:\\r\\n]*:(.+)`);
      const m = body.match(re);
      return m ? m[1].trim() : '';
    };
    const uid = get('UID');
    events.push({
      id: uid || `imported-${events.length}`,
      uid,
      title: unescapeText(get('SUMMARY')) || 'Imported event',
      start: parseIcsDate(get('DTSTART')),
      end: parseIcsDate(get('DTEND')),
      location: unescapeText(get('LOCATION')),
      notes: unescapeText(get('DESCRIPTION')),
      type: 'imported',
      source: 'imported',
      readOnly: true,
    });
  });
  return events;
}

/**
 * Merge imported events into existing ones — re-import UPDATES by UID rather
 * than duplicating (§4.9 acceptance: zero duplicates on re-import).
 */
export function mergeImportedEvents(existing = [], imported = []) {
  const byUid = new Map(existing.map((e) => [e.uid || e.id, e]));
  imported.forEach((e) => {
    byUid.set(e.uid || e.id, { ...byUid.get(e.uid || e.id), ...e });
  });
  return Array.from(byUid.values());
}
